import { ActionTree, ActionContext } from 'vuex'
import { getAccessToken, setAccessToken } from '@internetworkexpert/js-common'
import { fetchPaymentInfo, postCheckout, fetchCouponInfo } from '@/models/subscriptions'
import router from '@/router'
import { ISubscriptionsState, IRootState, ISubscriptionPayload, IChallengePayload } from '@/@types'
import { updateBrazeExternalId } from '@/utils'
import CONSTANTS from '@/constants'
import { eventsConstants } from '@/helpers/eventsList'

const planCodes = Object.values(CONSTANTS.PLANS)

interface IErrorNewUser extends Error {
  error: Error
  new_user?: {
    access_token: string
  }
  processor_error?: string
  processor_error_code?: string
  actionTokenId?: string
}

const actions: ActionTree<ISubscriptionsState, IRootState> = {
  submitPaymentInfo: async (
    context: ActionContext<ISubscriptionsState, IRootState>,
    params: {
      challengeToken: string | null
      captchaToken: string | null
    }
  ) => {
    const { challengeToken, captchaToken } = params
    const selectedPlan = context.rootGetters['shoppingCart/selectedPlan']
    const selectedSkillDivePlan = context.rootGetters['shoppingCart/selectedSkillDivePlan']
    const user_data = context.rootState.auth.userProfile ?? ''
    const coupon = context.rootState.shoppingCart?.coupon ?? ''
    const billing_token = context.state?.recurlyToken ?? ''
    const accessToken = getAccessToken()
    const filteredItems = context.rootState.shoppingCart.shoppingCart.filter(
      i => i && i.merchant_id && !planCodes.includes(i.merchant_id)
    )
    const items =
      filteredItems && filteredItems.length
        ? filteredItems.map(i => {
            return { code: i.merchant_id }
          })
        : []
    const utm = context.rootState.events.utmParams ?? {}
    const plans = []
    if (selectedPlan.merchant_id) {
      const plan = {
        code: selectedPlan.merchant_id,
        trial: selectedPlan.plan_trial ?? false,
        add_ons: [],
      }
      plans.push(plan)
    }
    if (selectedSkillDivePlan.merchant_id) {
      const plan = {
        code: selectedSkillDivePlan.merchant_id,
        trial: false,
        add_ons: selectedSkillDivePlan.addOnsOnCart.map(
          (SDAddOn: { merchant_id: any }) => SDAddOn.merchant_id
        ),
      }
      plans.push(plan)
    }

    const payload = {
      plans,
      coupon,
      billing_token,
      origin: process.env.VUE_APP_ORIGIN !== undefined ? process.env.VUE_APP_ORIGIN : '',
      items,
      utm,
      captchaToken,
    }
    try {
      if (accessToken) {
        const payloadWAccessToken: IChallengePayload = Object.assign({}, payload, {
          payment_challenge_token: challengeToken ?? '',
          accessToken,
        })
        return await postCheckout(payloadWAccessToken)
      }
      const payloadWUserData: ISubscriptionPayload = Object.assign({}, payload, {
        user_data,
      })
      const returnedResponse = await postCheckout(payloadWUserData)
      const { access_token, user_id } = returnedResponse
      updateBrazeExternalId(user_id)
      setAccessToken(access_token)
      return returnedResponse
    } catch (errorObject) {
      const error = errorObject as IErrorNewUser
      let attemptResponse
      const { processor_error_code } = error
      const isNewUser = error.new_user
      if (isNewUser) {
        const new_user_access_token = isNewUser.access_token
        setAccessToken(new_user_access_token)
        attemptResponse = await context.dispatch('auth/attemptLogin', new_user_access_token, {
          root: true,
        })
        await context.dispatch(
          'events/sendMixpanelEvent',
          { event_name: eventsConstants.CHECKOUT_USER_SIGNUP },
          {
            root: true,
          }
        )
        attemptResponse.user_id = attemptResponse?.id
      }
      const { message } = error.error ? error.error : (error as Error)
      if (message === 'The email has already been taken.') {
        router.push({
          name: 'account',
          params: { action: 'login' },
        })
        return context.dispatch(
          'app/setError',
          'Payment declined. The email has already been taken.',
          {
            root: true,
          }
        )
      }
      context.commit('THREE_DS_FLOW_UPDATED', false)
      if (processor_error_code && processor_error_code.length) {
        if (processor_error_code === 'three_d_secure_action_required') {
          const { actionTokenId } = error
          const td_access_token = getAccessToken()
          return context.dispatch('trigger3dsFlow', {
            actionTokenId,
            td_access_token,
          })
        }
        if (processor_error_code === 'declined') {
          context.dispatch(
            'app/setError',
            `Your charge has been declined. Please contact your bank if you believe this was a mistake.`,
            {
              root: true,
            }
          )
          if (isNewUser) {
            attemptResponse.error = true
            return attemptResponse
          }
          return false
        }
        context.dispatch(
          'app/setError',
          'There was an issue with the credit card provided. Please contact your bank.',
          {
            root: true,
          }
        )
        if (isNewUser) {
          attemptResponse.error = true
          return attemptResponse
        }
        return false
      }
      if (
        message === 'Token is either invalid or expired' ||
        message === "Trying to get property 'account_code' of non-object"
      ) {
        context.commit('RECURLY_TOKEN_UPDATED', '')
        router.push({ name: 'payment' })
        return context.dispatch(
          'app/setError',
          'Session expired. Please re-enter your payment details.',
          {
            root: true,
          }
        )
      }
      context.dispatch('app/setError', message, { root: true })
    } finally {
      context.dispatch('app/setIsComponentLoading', false, { root: true })
    }
  },
  setPartnerStackFlow: (
    context: ActionContext<ISubscriptionsState, IRootState>,
    payload: boolean
  ) => {
    context.commit('PARTNER_STACK_FLOW_UPDATED', payload)
  },
  trigger3dsFlow: (context: ActionContext<ISubscriptionsState, IRootState>, { actionTokenId }) => {
    context.commit('RECURLY_ACTION_TOKEN_ID_UPDATED', actionTokenId)
    context.commit('THREE_DS_FLOW_UPDATED', true)
  },
  resolve3dsFlow: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.dispatch('app/setIsComponentLoading', true, { root: true })
    context.commit('RECURLY_ACTION_TOKEN_RESULT_UPDATED', payload)
    context.commit('THREE_DS_FLOW_UPDATED', false)
  },
  getCurrentPaymentMethodOnFile: async (
    context: ActionContext<ISubscriptionsState, IRootState>
  ) => {
    try {
      const method = await fetchPaymentInfo()
      context.commit('PAYMENT_METHOD_ON_FILE_UPDATED', method)
      return method
    } catch (error) {
      context.commit('PAYMENT_METHOD_ON_FILE_UPDATED', '')
    }
  },
  getCouponInformation: async (
    context: ActionContext<ISubscriptionsState, IRootState>,
    couponCode: string
  ) => {
    try {
      const couponInformation = await fetchCouponInfo(couponCode)
      context.commit('COUPON_INFORMATION_UPDATED', couponInformation)
      return couponInformation
    } catch (error) {
      context.commit('COUPON_INFORMATION_UPDATED', error)
    }
  },
  removePaymentMethodOnFile: async (context: ActionContext<ISubscriptionsState, IRootState>) => {
    context.commit('PAYMENT_METHOD_ON_FILE_UPDATED', '')
  },
  updatePaymentEntries: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('PAYMENT_ENTRIES_UPDATED', payload)
  },
  updateCheckoutDiscount: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('CHECKOUT_DISCOUNT_UPDATED', payload)
  },
  updateCheckoutTaxes: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('CHECKOUT_TAXES_UPDATED', payload)
  },
  updateCheckoutTotal: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('CHECKOUT_TOTAL_UPDATED', payload)
  },
  setRecurlyToken: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('RECURLY_TOKEN_UPDATED', payload)
  },
  setPaymentMethod: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('PAYMENT_METHOD_UPDATED', payload)
  },
  setPromotionalField: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('PROMOTIONAL_FIELD_UPDATED', payload)
  },
}

export default actions
